import { Grid, GridConfig } from '../core/Grid.js';
import { GridGenerator } from '../core/GridGenerator.js';
import { CLASSIC_GAME_TYPES, CLASSIC_SCOPE } from '../persistence/types.js';
import { GameSetPicker } from './GameSetPicker.js';

export interface GridCreatorCallbacks {
    /** `gameSet` is the chosen scope string, or undefined for the classic 12. */
    onGridCreated: (grid: Grid, gameSet?: string) => void;
    onClose: () => void;
    isVortexUnlocked: (config: GridConfig) => boolean;
}

interface PresetConfig {
    key: string;
    config: GridConfig;
    label: string;
    description: string;
    unlockHint?: string;  // Shown when locked
}

export class GridCreator {
    private containerEl: HTMLElement;
    private presets: Map<string, Grid> = new Map();
    private callbacks: GridCreatorCallbacks;

    /**
     * The 12-game set the created grid will play; defaults to classic 12. Changing it
     * regenerates the previews, so they reflect the rules that set actually needs — a set
     * without Jumps, say, can show grids with three digits at one quarter-position.
     */
    private setPicker: GameSetPicker = new GameSetPicker(
        CLASSIC_GAME_TYPES,
        () => this.onSetChanged(),
    );

    private static readonly BASE_PRESETS: PresetConfig[] = [
        { key: '10x10_1-9', config: { size: 10, min: 1, max: 9 }, label: '10×10, digits 1-9', description: 'Standard' },
        { key: '8x8_1-6', config: { size: 8, min: 1, max: 6 }, label: '8×8, digits 1-6', description: 'Medium' },
        { key: '6x6_1-3', config: { size: 6, min: 1, max: 3 }, label: '6×6, digits 1-3', description: 'Tutorial' },
    ];

    private static readonly VORTEX_PRESETS: PresetConfig[] = [
        { key: '10x10_0-9_v', config: { size: 10, min: 0, max: 9, isVortex: true }, label: '10×10, digits 0-9', description: 'Vortex Standard', unlockHint: 'Clear 3 Standard challenges to unlock' },
        { key: '8x8_0-6_v', config: { size: 8, min: 0, max: 6, isVortex: true }, label: '8×8, digits 0-6', description: 'Vortex Medium', unlockHint: 'Clear 3 Medium challenges to unlock' },
        { key: '6x6_0-3_v', config: { size: 6, min: 0, max: 3, isVortex: true }, label: '6×6, digits 0-3', description: 'Vortex Tutorial' },
    ];

    constructor(containerEl: HTMLElement, callbacks: GridCreatorCallbacks) {
        this.containerEl = containerEl;
        this.callbacks = callbacks;
    }

    show(): void {
        this.setPicker.setSelection(CLASSIC_GAME_TYPES);
        this.generatePresets();
        this.render();
        this.containerEl.classList.remove('hidden');
    }

    hide(): void {
        this.presets.clear();
        this.containerEl.classList.add('hidden');
    }

    /** Regenerate the previews under the newly-chosen set and re-render. */
    private onSetChanged(): void {
        this.generatePresets();
        this.render();
    }

    private generatePresets(): void {
        this.presets.clear();
        const games = this.setPicker.getSelection();
        for (const { key, config } of GridCreator.BASE_PRESETS) {
            const grid = new GridGenerator(config, games).generate();
            this.presets.set(key, grid);
        }
        // Only generate Vortex grids if unlocked
        for (const { key, config } of GridCreator.VORTEX_PRESETS) {
            if (this.callbacks.isVortexUnlocked(config)) {
                const grid = new GridGenerator(config, games).generate();
                this.presets.set(key, grid);
            }
        }
    }

    private render(): void {
        this.containerEl.innerHTML = '';

        // The picker owns its own element, so re-appending after a Refresh keeps the set.
        this.containerEl.appendChild(this.setPicker.element);

        const optionsList = document.createElement('div');
        optionsList.className = 'grid-options-list grid-creator-list';

        // Base presets
        for (const preset of GridCreator.BASE_PRESETS) {
            optionsList.appendChild(this.renderPresetCard(preset, true));
        }

        // Vortex presets
        for (const preset of GridCreator.VORTEX_PRESETS) {
            const unlocked = this.callbacks.isVortexUnlocked(preset.config);
            optionsList.appendChild(this.renderPresetCard(preset, unlocked));
        }

        this.containerEl.appendChild(optionsList);

        // Refresh button footer
        const footer = document.createElement('div');
        footer.className = 'grid-creator-footer';

        const refreshBtn = document.createElement('button');
        refreshBtn.className = 'grid-creator-refresh-btn';
        refreshBtn.textContent = 'Refresh';
        refreshBtn.addEventListener('click', () => {
            this.generatePresets();
            this.render();
        });

        footer.appendChild(refreshBtn);
        this.containerEl.appendChild(footer);
    }

    private renderPresetCard(preset: PresetConfig, unlocked: boolean): HTMLElement {
        const { key, label, description, unlockHint } = preset;
        const isVortex = !!preset.config.isVortex;

        const card = document.createElement('div');
        card.className = 'grid-creator-card';
        if (isVortex) card.classList.add('vortex');
        if (!unlocked) card.classList.add('locked');

        // Preview canvas
        const preview = document.createElement('canvas');
        preview.className = 'grid-creator-preview';
        preview.width = 160;
        preview.height = 160;
        const grid = this.presets.get(key);
        if (grid) {
            this.renderPreview(preview, grid, isVortex);
        } else {
            this.renderLockedPreview(preview, preset.config.size);
        }
        card.appendChild(preview);

        // Title section
        const titleSection = document.createElement('div');
        titleSection.className = 'grid-creator-card-title';

        const descEl = document.createElement('div');
        descEl.className = 'grid-creator-description';
        descEl.textContent = description;

        const labelEl = document.createElement('div');
        labelEl.className = 'grid-creator-label';
        labelEl.textContent = label;

        titleSection.appendChild(descEl);
        titleSection.appendChild(labelEl);

        if (!unlocked && unlockHint) {
            const hintEl = document.createElement('div');
            hintEl.className = 'grid-creator-locked-label';
            hintEl.textContent = unlockHint;
            titleSection.appendChild(hintEl);
        }

        card.appendChild(titleSection);

        // Click handler for unlocked cards
        if (unlocked) {
            card.addEventListener('click', () => {
                this.selectPreset(key);
            });
        }

        return card;
    }

    private renderPreview(canvas: HTMLCanvasElement, grid: Grid, isVortex: boolean): void {
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const size = canvas.width;
        const cellSize = size / grid.size;

        // Background
        ctx.fillStyle = '#1a1a2e';
        ctx.fillRect(0, 0, size, size);

        // Grid lines (subtle)
        ctx.strokeStyle = '#2a2a4e';
        ctx.lineWidth = 0.5;
        for (let i = 0; i <= grid.size; i++) {
            ctx.beginPath();
            ctx.moveTo(i * cellSize, 0);
            ctx.lineTo(i * cellSize, size);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(0, i * cellSize);
            ctx.lineTo(size, i * cellSize);
            ctx.stroke();
        }

        // Fill digit cells with colored squares
        ctx.fillStyle = isVortex ? '#a78bfa' : '#4ecdc4';

        for (let digit = grid.minDigit; digit <= grid.maxDigit; digit++) {
            const pos = grid.getDigitPosition(digit);
            if (pos) {
                const x = pos.col * cellSize;
                const y = pos.row * cellSize;
                ctx.fillRect(x + 1, y + 1, cellSize - 2, cellSize - 2);
            }
        }
    }

    private renderLockedPreview(canvas: HTMLCanvasElement, gridSize: number): void {
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const size = canvas.width;
        const cellSize = size / gridSize;

        ctx.fillStyle = '#1a1a2e';
        ctx.fillRect(0, 0, size, size);

        ctx.strokeStyle = '#2a2a4e';
        ctx.lineWidth = 0.5;
        for (let i = 0; i <= gridSize; i++) {
            ctx.beginPath();
            ctx.moveTo(i * cellSize, 0);
            ctx.lineTo(i * cellSize, size);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(0, i * cellSize);
            ctx.lineTo(size, i * cellSize);
            ctx.stroke();
        }
    }

    private selectPreset(presetKey: string): void {
        const grid = this.presets.get(presetKey);
        if (!grid) return;

        const scope = this.setPicker.getScope();
        this.callbacks.onGridCreated(grid, scope === CLASSIC_SCOPE ? undefined : scope);
        this.hide();
        this.callbacks.onClose();
    }
}
